import type { AgeBand, DrillColumn, DrillThroughRow } from "@/types/drillThroughTable";
import { displayAgeBandLabel } from "@/lib/ageBandLabels";

const DEFAULT_AGE_BANDS: AgeBand[] = [
  { key: "age_0_30", label: "0-30" },
  { key: "age_31_60", label: "31-60" },
  { key: "age_61_90", label: "61-90" },
  { key: "age_91_120", label: "91-120" },
  { key: "age_120_180", label: displayAgeBandLabel("120-180") },
  { key: "age_180_plus", label: "180+" },
];

const AGE_KEY_BY_LABEL: Record<string, string> = {
  "0-30": "age_0_30",
  "31-60": "age_31_60",
  "61-90": "age_61_90",
  "91-120": "age_91_120",
  "120-180": "age_120_180",
  "180+": "age_180_plus",
  "180 plus": "age_180_plus",
};

export function getSelectedMonth(searchParams: URLSearchParams): string | null {
  const raw = searchParams.get("month");
  if (!raw) return null;
  const normalized = raw.split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;
  if (/^\d{4}-\d{2}$/.test(normalized)) return `${normalized}-01`;
  return normalized;
}

export function formatMonthLabel(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(`${value.split("T")[0]}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", { month: "short", year: "numeric" }).format(date);
}

export function normalizeAgeBands(ageBands?: Array<string | { key?: string; label?: string; name?: string }>): AgeBand[] {
  if (!ageBands?.length) return DEFAULT_AGE_BANDS;

  return ageBands
    .map((band) => {
      const rawKey = typeof band === "string" ? band : band.key || band.name || band.label || "";
      const rawLabel = typeof band === "string" ? band : band.label || band.name || band.key || "";
      const key = getAgeKey(rawKey) || getAgeKey(rawLabel);
      return key ? { key, label: displayAgeBandLabel(cleanAgeLabel(rawLabel || key)) } : null;
    })
    .filter(Boolean) as AgeBand[];
}

export function createValueColumns(ageBands: AgeBand[]): DrillColumn[] {
  return [
    ...ageBands.map((band) => ({ ...band, kind: "money" as const, sortKey: band.key })),
    { key: "total", label: "Total", kind: "money", sortKey: "total" },
  ];
}

export function getColumnWidth(column: DrillColumn, columns: DrillColumn[]): string {
  const textColumns = columns.filter((item) => item.kind === "text").length;
  if (column.kind === "text") return textColumns > 1 ? "19%" : "24%";
  return `${Math.max(7, Math.floor((100 - textColumns * (textColumns > 1 ? 19 : 24)) / (columns.length - textColumns)))}%`;
}

export function getTableMinWidth(textColumns: number, valueColumns: number): number {
  const TEXT_COLUMN_MIN_PX = 160;
  const VALUE_COLUMN_MIN_PX = 96;
  return textColumns * TEXT_COLUMN_MIN_PX + valueColumns * VALUE_COLUMN_MIN_PX;
}

export function getCellValue(row: DrillThroughRow, key: string) {
  const value = row[key];
  return value === undefined || value === "" ? null : value;
}

export function formatCurrency(value: unknown, maximumFractionDigits = 0): string {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  const formatted = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits,
    minimumFractionDigits: 0,
  }).format(Math.abs(number));
  return number < 0 ? `(${formatted})` : formatted;
}

export function formatText(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}

export function getRowKey(row: DrillThroughRow, rowIndex: number): string {
  const stable =
    row.id ??
    row.manager ??
    row.assignment_manager ??
    row.client_name ??
    row.assignment_name;
  return stable ? `${String(stable)}-${rowIndex}` : String(rowIndex);
}

function getAgeKey(value: string): string | null {
  const normalized = value
    .replace(/^age[_\s-]*/i, "")
    .replace(/_/g, "-")
    .replace(/\s+/g, " ")
    .toLowerCase();
  const direct = AGE_KEY_BY_LABEL[normalized] || AGE_KEY_BY_LABEL[normalized.replace("-", " ")];
  if (direct) return direct;
  if (DEFAULT_AGE_BANDS.some((band) => band.key === value)) return value;
  return null;
}

function cleanAgeLabel(value: string): string {
  return value
    .replace(/^age[_\s-]*/i, "")
    .replace(/_/g, "-")
    .replace(/180-plus/i, "180+")
    .replace(/180 plus/i, "180+");
}
